import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import React, { useCallback, useMemo, useState } from 'react';

import { Tooltip } from 'src/components/tooltip';

import { useStyles } from '../DataTable.styles';
import {
    toCellString,
    type DataTableCellShell,
    type DataTableCellValue,
    type DataTableRow,
} from '../DataTable.types';

export interface DataTableBodyCellProps {
    shell: DataTableCellShell;
    row: DataTableRow;
    cellKey: string;
    isCopied: boolean;
    copiedLabel: string;
    clickToCopyLabel: string;
    onCopy: (key: string, value: DataTableCellValue) => void;
    onRowClick?: (row: DataTableRow) => void;
}

const DataTableBodyCellInner = ({
    shell,
    row,
    cellKey,
    isCopied,
    copiedLabel,
    clickToCopyLabel,
    onCopy,
    onRowClick,
}: DataTableBodyCellProps) => {
    const { classes, cx } = useStyles();
    const { column } = shell;
    const value = row[column.field];
    const display = column.valueFormatter ? column.valueFormatter(value, row) : toCellString(value);
    const hasCustomRender = column.renderCell !== undefined;
    const isCopyable = column.copyable !== false && !hasCustomRender && display !== '';

    const [tooltipArmed, setTooltipArmed] = useState(false);

    const cellClassName = useMemo(
        () =>
            cx(
                classes.cell,
                shell.isFirst && classes.cellFirst,
                column.wrapText && classes.cellWrap,
                column.noBorderRight && classes.cellNoBorderRight,
            ),
        [
            cx,
            classes.cell,
            classes.cellFirst,
            classes.cellWrap,
            classes.cellNoBorderRight,
            shell.isFirst,
            column.wrapText,
            column.noBorderRight,
        ],
    );

    const handleCellClick = useMemo(() => {
        if (column.onCellClick) return () => column.onCellClick!(row);
        if (onRowClick) return () => onRowClick(row);
        return undefined;
    }, [column, onRowClick, row]);

    const cellStyle = useMemo(
        () => ({
            width: shell.width,
            cursor: handleCellClick ? ('pointer' as const) : undefined,
        }),
        [shell.width, handleCellClick],
    );

    const triggerCopy = useCallback(
        (e: React.SyntheticEvent) => {
            e.stopPropagation();
            onCopy(cellKey, value);
        },
        [onCopy, cellKey, value],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                triggerCopy(e);
            }
        },
        [triggerCopy],
    );

    const armTooltip = useCallback(() => setTooltipArmed(true), []);

    // Custom-rendered cells (badges, chips, etc.) bypass the copy affordance
    // and tooltip plumbing entirely — they render their own content centered
    // in the cell and inherit the row's click handler.
    if (hasCustomRender) {
        return (
            <TableCell className={cellClassName} style={cellStyle} onClick={handleCellClick}>
                <Box
                    component="span"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                    }}
                >
                    {column.renderCell!(row)}
                </Box>
            </TableCell>
        );
    }

    const isLink = !isCopyable && !!handleCellClick && display !== '';
    const textClassName = cx(
        classes.cellText,
        column.wrapText && classes.cellTextWrap,
        isLink && classes.cellLink,
    );

    const cellText = (
        <Box
            component="span"
            className={textClassName}
            onMouseEnter={isCopyable && !tooltipArmed ? armTooltip : undefined}
            onFocus={isCopyable && !tooltipArmed ? armTooltip : undefined}
            {...(isCopyable
                ? {
                      role: 'button' as const,
                      tabIndex: 0,
                      onClick: triggerCopy,
                      onKeyDown: handleKeyDown,
                  }
                : undefined)}
        >
            {display}
        </Box>
    );

    const tooltipTitle = (() => {
        if (isCopyable && tooltipArmed) return isCopied ? copiedLabel : clickToCopyLabel;
        if (column.tooltipText && handleCellClick) return column.tooltipText;
        return undefined;
    })();

    return (
        <TableCell className={cellClassName} style={cellStyle} onClick={handleCellClick}>
            {tooltipTitle === undefined ? (
                cellText
            ) : (
                <Tooltip title={tooltipTitle}>{cellText}</Tooltip>
            )}
        </TableCell>
    );
};

export const DataTableBodyCell = React.memo(DataTableBodyCellInner);
